import React, { useMemo } from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';

import { IconText, TickCircleIcon, WarningIcon } from '@motorway/mw-highway-code';

import cypressIds from 'Components/cypress_ids.json';
import { VehicleSpecsDetailsButton } from 'Components/pages/VehicleDetailsRedesign/sections/MotSection/VehicleSpecsDetailsButton/VehicleSpecsDetailsButton';
import HistoryCheckModal from 'Components/VehicleHistory/HistoryCheckModal/HistoryCheckModal';
import type { Vehicle } from 'Types/vehicle';
import useVehicleHistoryChecks from 'Utilities/hooks/vehicle/useVehicleHistoryChecks';

import LocalTexts from './HistoryCheckField.texts';

import styles from './HistoryCheckField.module.scss';

const LocalT = new MDText(LocalTexts);

export const HISTORY_CHECK_EVENT_CATEGORY = 'Vehicle details';

export const HISTORY_CHECK_EVENT_ACTION = Object.freeze({
	historyCheckModalClosed: 'History check modal closed',
	historyCheckModalOpened: 'History check modal opened',
});

interface HistoryCheckFieldProps {
	events: {
		onModalClose: () => void;
		onModalOpen: () => void;
	};
	historyChecks: Vehicle['historyChecks'];
	make: Vehicle['spec']['make'];
	model: Vehicle['spec']['model'];
	prettyVrm: string;
}

const HistoryCheckField: React.FC<HistoryCheckFieldProps> = ({ events, historyChecks, make, model, prettyVrm }) => {
	const { alertCount, hasAlerts } = useVehicleHistoryChecks(historyChecks);

	const fieldConfig = useMemo(
		() => ({
			success: {
				className: styles.success,
				icon: TickCircleIcon,
				text: LocalT.translate('success'),
			},
			warning: {
				className: styles.warning,
				icon: WarningIcon,
				text: LocalT.translate(`alert.${alertCount === 1 ? 'singular' : 'plural'}`, { alertCount }),
			},
		}),
		[alertCount],
	);

	const { className, icon, text } = hasAlerts ? fieldConfig.warning : fieldConfig.success;
	return (
		<div className={cx(styles.historyCheck, className)}>
			<IconText icon={icon} text={text} variant="small--regular" />
			<VehicleSpecsDetailsButton
				cypressId={cypressIds.links.historyCheckDetails}
				modal={<HistoryCheckModal historyChecks={historyChecks} make={make} model={model} prettyVrm={prettyVrm} />}
				onModalClose={events.onModalClose}
				onModalOpen={events.onModalOpen}
			/>
		</div>
	);
};

export default HistoryCheckField;
